/**
 * shadcn Button — base primitive used by other shadcn components (Calendar, Dialog, etc.).
 *
 * Source: `npx shadcn@latest add button` (base-nova style, Tailwind v4).
 * Adapted: uses `react-icons/lu` instead of `lucide-react`.
 * Uses Base UI (`@base-ui/react`) instead of Radix for the button primitive.
 *
 * `buttonVariants` is exported separately so other components (e.g. Calendar nav buttons)
 * can apply button styling without rendering a <Button> element.
 *
 * Polymorphism: use `buttonVariants` with a plain element instead of `render` prop.
 * Do NOT use `<Button render={<a />} nativeButton={false} />` for links — Base UI's
 * Button always applies `role="button"`, overriding semantic link role on `<a>`.
 *
 * Variants: default | destructive | outline | secondary | muted | ghost | link
 * Sizes:    default | xs | sm | lg | icon | icon-xs | icon-sm | icon-lg
 *
 * @see docs/styling.md
 */
'use client';

import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cn } from '@web-app/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-[10px] text-sm font-semibold whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-primary-foreground hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40',
        outline:
          'border border-border-cool bg-card text-foreground shadow-default-xs font-medium hover:bg-muted hover:text-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        muted: 'bg-muted text-muted-foreground hover:bg-muted/80',
        ghost:
          'text-primary hover:bg-muted hover:text-primary dark:hover:bg-muted',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-6 py-2.5 has-[>svg]:px-4',
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        lg: 'h-10 px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-xs': "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Button, buttonVariants };
